import { useLogin } from "@/context/useLoginProps";
import { getClientId } from "@/utils";
import {
  Button,
  Eye,
  EyeHide,
  Input,
  Label,
} from "@monsterindia/global-ui-components";
import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import Heading from "../../common/heading";
import { useLoginUser } from "../query";

const ChangePassword: React.FC = () => {
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const params = new URLSearchParams(window.location.search);
  const {
    showToast,
    loginData: { username, countryCode, selectedEmailFromPopup, isEmailLogin },
    translation: { login, errorMessages },
    handleEventTracking,
  } = useLogin();

  const { mutate: loginUser, data, status, error: loginError } = useLoginUser();
  const isLoading = status === "pending";
  const {
    control,
    register,
    getValues,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
    mode: "onBlur",
  });

  const newPassword = useWatch({ control, name: "newPassword" });
  const confirmPassword = useWatch({ control, name: "confirmPassword" });

  useEffect(() => {
    if (data?.redirectUrl) {
      window.location.href = data?.redirectUrl;
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_ResetPassword_Success",
        eventProps: {
          status: "success",
        },
      });

      isEmailLogin &&
        handleEventTracking({
          eventType: "onUserLoginPush",
          eventProps: {
            email: username,
          },
        });
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_Completed",
        eventProps: {
          method: "changePassword",
        },
      });
    } else if (data?.error?.message?.errorMessage) {
      showToast({
        show: true,
        type: "error",
        message: data?.error?.message?.errorMessage,
      });
    }
  }, [data]);

  useEffect(() => {
    if (loginError) {
      showToast({
        show: true,
        type: "error",
        message:
          loginError instanceof Error
            ? loginError.message
            : "Login failed. Please try again.",
      });
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_ResetPassword_Error",
        eventProps: {
          error_type: loginError.message ?? "Unknown Error",
        },
      });
    }
  }, [loginError]);

  useEffect(() => {
    handleEventTracking({
      eventType: "eventPush",
      eventName: "Login_ResetPassword_Page_Viewed",
      eventProps: {
        stage: "create_password",
      },
    });
  }, []);

  const onSubmit = (data: any) => {
    if (newPassword === confirmPassword) {
      const client_id = params.get("client_id");
      handleEventTracking({
        eventType: "eventPush",
        eventName: "Login_ResetPassword_Submitted",
        eventProps: {
          pwd_length: data.newPassword.length,
        },
      });
      const queryParams = new URLSearchParams(window.location.search);
      loginUser(
        {
          username: isEmailLogin ? username : countryCode + username,
          new_password: data.newPassword,
          client_id: getClientId(client_id || undefined),
          ...(selectedEmailFromPopup?.uuid && {
            uuid: selectedEmailFromPopup?.uuid,
          }),
          queryParams,
        },
        {
          onSuccess: () => {
            showToast({
              show: true,
              type: "success",
              message: login.passwordChangedSuccessfully,
            });
          },
          onError: (error) => {
            showToast({
              show: true,
              type: "error",
              message: error.message,
            });
          },
        },
      );
    } else {
      showToast({
        show: true,
        type: "error",
        message: errorMessages.passwordMismatch,
      });
    }
  };

  return (
    <>
      <Heading title={login.createPassword} />

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mt-8 flex flex-col gap-8"
      >
        <Label
          labelText={login.setNewPassword}
          className="text-sm"
          htmlFor="newPassword"
        >
          <Input
            id="newPassword"
            type={showPassword.newPassword ? "text" : "password"}
            placeholder={login.minimumCharacters}
            errorText={errors.newPassword?.message}
            {...register("newPassword", {
              required: {
                value: true,
                message: errorMessages.fieldRequired,
              },
              minLength: {
                value: 6,
                message: login.minLength,
              },
            })}
            rightIcon={
              <button
                type="button"
                onClick={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    newPassword: !prev.newPassword,
                  }))
                }
                className="cursor-pointer text-gray-500 hover:text-gray-700"
              >
                {showPassword.newPassword ? (
                  <EyeHide width={20} height={20} />
                ) : (
                  <Eye width={20} height={20} />
                )}
              </button>
            }
          />
        </Label>

        <Label
          labelText={login.confirmPassword}
          className="text-sm"
          htmlFor="confirmPassword"
        >
          <Input
            id="confirmPassword"
            type={showPassword.confirmPassword ? "text" : "password"}
            placeholder={login.reEnterPassword}
            errorText={errors.confirmPassword?.message}
            {...register("confirmPassword", {
              required: {
                value: true,
                message: errorMessages.fieldRequired,
              },
              validate: (value) =>
                value === getValues("newPassword") ||
                errorMessages.passwordMismatch,
            })}
            rightIcon={
              <button
                type="button"
                onClick={() =>
                  setShowPassword((prev) => ({
                    ...prev,
                    confirmPassword: !prev.confirmPassword,
                  }))
                }
                className="cursor-pointer text-gray-500 hover:text-gray-700"
              >
                {showPassword.confirmPassword ? (
                  <EyeHide width={20} height={20} />
                ) : (
                  <Eye width={20} height={20} />
                )}
              </button>
            }
          />
        </Label>

        <Button
          size="lg"
          type="submit"
          id="loginSubmit"
          modifier="primary"
          aria-label="submit"
          className="rounded-full"
          disabled={!newPassword || !confirmPassword}
          label={login.changePassword}
          isLoading={isLoading}
        />
      </form>
    </>
  );
};

export default ChangePassword;
